import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'

import { AppButton } from '@/components'
import { BaseView } from '@/components/base'
import { TextInputForm } from '@/components/form'
import { inputLimits } from '@/constants/inputLimits'
import { useSignInWithEmail } from '@/domain'
import { IEmailSchema, useEmailSchema } from '@/schemas'
import { useAuthPreferencesStore } from '@/store'
import { useAuthScopedStore } from '@/store/scoped'

export const IdentifyWithEmailForm = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const setEmail = useAuthScopedStore((state) => state.setEmail)
  const setIdentificationMethod = useAuthScopedStore(
    (state) => state.setIdentificationMethod,
  )
  const setSignUpForm = useAuthScopedStore((state) => state.setSignUpForm)
  const setSignInForm = useAuthScopedStore((state) => state.setSignInForm)

  const emailInAuthPreferences = useAuthPreferencesStore((state) => state.email)
  const setAuthPreferences = useAuthPreferencesStore(
    (state) => state.setAuthPreferences,
  )

  const emailSchema = useEmailSchema()

  const { handleSubmit, control, formState, trigger, setValue } =
    useForm<IEmailSchema>({
      resolver: zodResolver(emailSchema),
      defaultValues: {
        email: emailInAuthPreferences || '',
      },
      mode: 'onBlur',
    })

  const { mutateAsync: signInWithEmail, isPending: isCheckingEmail } =
    useSignInWithEmail({
      onSuccess: ({ user_id, next_step }) => {
        //LOGIN
        if (next_step === 'VERIFY_PASSWORD') {
          setSignInForm({
            userId: user_id,
          })

          navigate('/auth/enter-password')
          //SIGNUP
        } else if (next_step === 'VERIFY_CODE') {
          setSignUpForm({
            userId: user_id,
          })

          navigate('/auth/signup/enter-verification-code')
        }
      },
      onError: ({ errorMessage }) => {
        if (!errorMessage) return

        setEmail(undefined)

        toast.error(errorMessage)
      },
    })

  const handleSubmitForm = async (formData: IEmailSchema) => {
    setEmail(formData.email)
    setIdentificationMethod('email')
    setAuthPreferences({
      email: formData.email,
    })

    signInWithEmail({
      email: formData.email,
    })
  }

  return (
    <>
      <TextInputForm
        control={control}
        name="email"
        label={t('translation:email')}
        maxLength={inputLimits.email.max}
        keyboardType="email-address"
        autoComplete="email"
        onChangeText={(value) => {
          setValue('email', value, {
            shouldValidate: value.includes('@'),
          })
        }}
        onBlur={() => trigger('email')}
      />

      <BaseView mt="auto" row align="center" gap={12} className="mb-3">
        <AppButton
          label={t('form:actions.usePhone')}
          disabled={isCheckingEmail}
          type="secondaryGrayDark"
          onPress={() => {
            setIdentificationMethod('phone')
            setAuthPreferences({
              signInMethod: 'phone',
            })

            navigate('/auth/identifier/with-phone')
          }}
          style={{ flex: 1 }}
        />
        <AppButton
          label={t('form:actions.next')}
          onPress={handleSubmit(handleSubmitForm)}
          disabled={!formState.isValid}
          isLoading={isCheckingEmail}
          style={{ flex: 1 }}
        />
      </BaseView>
    </>
  )
}
